<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, buildPageFooter, isButtonDisabled } from '../functionCollection.js';
    import SearchFields from './SearchFields.vue';

    const props = defineProps(['fetchUrl', 'editUrl']);
    const usersList = ref(null);
    const columnsToFetch = "Rechnungsempfaengernummer,resb,Name1,Name2,PLZ,Ort,Email";

    // for the page footer
    const currentPage = ref(1);
    const maxPageNumber = ref(1);
    const usersOnPage = ref(null);
    const footerPageNumbers = ref(new Array());

    // for SearchFields. fields defined below in filterParameters. index is column name from the database.
    const filtersAsString = ref('');
    const receivedFilterParameters = ref([]);
    const filterParameters = [
        { index: 'Rechnungsempfaengernummer', title: 'Kundennummer', value: null },
        { index: 'resb', title: 'SB-Kundennummer', value: null },
        { index: 'Name1', title: 'Nachname', value: null },
        { index: 'Name2', title: 'Vorname', value: null },
        { index: 'Email', title: 'E-Mail', value: null },
        { index:'Matchcode', title: 'Matchcode', value: null },
        { index: 'PLZ', title: 'PLZ', value: null },
        { index: 'Ort', title: 'Ort', value: null },
        { index: 'Strasse', title: 'Straße', value: null }
    ];

    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl);
        usersList.value = await getFromAPI(url);

        maxPageNumber.value = usersList.value.meta.last_page;

        usersList.value = usersList.value.data;
        usersOnPage.value = usersList.value.length;
        footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
    })

    // add URL parameters to the URL prop. If we search, add the filters as well.
    function getFullUrl(url) {
        url = url.concat('/').concat(`?page=${currentPage.value}&per_page=25&columns=${columnsToFetch}&column=Rechnungsempfaengernummer&order=desc`);
        if (filtersAsString.value !== '') {
            url = url.concat('&filter=').concat(filtersAsString.value);
        }
        return url;
    }

    // replace the placeholder ':id' with the actual ID.
    function replaceIDinUrl(url, id) {
        let formattedUrl = url.replace(':Rechnungsempfaengernummer', id);
        return formattedUrl;
    }

    // calls for new usersList whenever a page change is requested (i.e. a page button is pressed in the footer)
    async function getNewPage(newPage) {
        currentPage.value = newPage;
        if (currentPage.value < 1) {
            currentPage.value = 1;
        }
        usersList.value = await getFromAPI(getFullUrl(props.fetchUrl));
        maxPageNumber.value = usersList.value.meta.last_page;
        usersList.value = usersList.value.data;
        usersOnPage.value = usersList.value.length;
        buildPageFooter(currentPage.value, maxPageNumber.value);
    }

    // builds search query URL parameters, removing empty fields from query, and executes the search.
    function filterSearch(givenFields) {
        receivedFilterParameters.value = [];
        for (let i = 0; i < givenFields.length; i++) {
            if (givenFields[i].value !== null) {
                receivedFilterParameters.value.push(givenFields[i]);
            }
            if (givenFields[i].index === 'suspended') {
                if (givenFields[i].value === 'Ja' || givenFields[i].value === 'ja') {
                    givenFields[i].value = 1;
                } else if (givenFields[i].value === 'Nein' || givenFields[i].value === 'nein') {
                    givenFields[i].value = 0;
                }
            }
        }
        filtersAsString.value = JSON.stringify(receivedFilterParameters.value);
        getNewPage(1);
    }

</script>
<template>
    <SearchFields
        :fields="filterParameters"
        @filters="filterSearch"
    />
    <div class="main-table" v-if="usersList && usersList.length > 0">
        <div class="inner-table">
            <table class="table">
                <thead>
                    <tr>
                        <th class="table-head">Kundennummer</th>
                        <th class="table-head">SB-Kundennummer</th>
                        <th class="table-head">Nachname</th>
                        <th class="table-head">Vorname</th>
                        <th class="table-head">PLZ</th>
                        <th class="table-head">Ort</th>
                        <th class="table-head">E-Mail</th>
                        <th class="table-head">Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="m-datatable__row" v-for="user of usersList" :key="user.id">
                        <td>{{ user.Rechnungsempfaengernummer }}</td>
                        <td>{{ user.resb }}</td>
                        <td>{{ user.Name1 }}</td>
                        <td>{{ user.Name2 }}</td>
                        <td>{{ user.PLZ }}</td>
                        <td>{{ user.Ort }}</td>
                        <td>{{ user.Email }}</td>
                        <td>
                            <div class="buttons">
                                <a  class="btn btn-primary"
                                    :href="replaceIDinUrl(props.editUrl, user.Rechnungsempfaengernummer)"
                                    role="button">
                                    Bearbeiten
                                </a>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="table-footer middle" v-if="usersList && usersList.length > 0 && footerPageNumbers.length > 1">
        <ul class="pagination">
            <li class="page-item">
                <button class="page-link" :disabled="isButtonDisabled('start')" @click="getNewPage(currentPage - 1)">
                    <i class="fas fa-lg fa-caret-left"></i>
                </button>
            </li>
            <li v-for="page in footerPageNumbers" :key="page.id" :class="{ 'active' : page == currentPage }" >
                <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page)">{{ page }}</button>
            </li>
            <li class="page-item">
                <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end')">
                    <i class="fas fa-lg fa-caret-right"></i>
                </button>
            </li>
        </ul>
    </div>

    <div class="m-3" v-if="usersList && (usersList.length == 0 || usersList == null)">
        <div class="alert alert-custom" role="alert">
            <i class="fa-solid fa-comment fa-4x"></i>
            <span>Es wurden keine Kunden gefunden.</span>
        </div>
    </div>

</template>
